import { isTeamsContext } from '@/utils/auth-context';

const SSO_SESSION_INTENT_KEY = 'lift-sso-session-intent';
const TEAMS_SSO_IN_PROGRESS_KEY = 'lift-teams-sso-in-progress';
const TEAMS_SSO_HANDOFF_KEY = 'lift-teams-sso-handoff';
const TEAMS_SSO_RELOADING_KEY = 'lift-teams-sso-reloading';
const TEAMS_SSO_IN_PROGRESS_TTL_MS = 5 * 60 * 1000;

function persistSsoSessionIntent(): void {
  sessionStorage.setItem(SSO_SESSION_INTENT_KEY, '1');

  // Teams iframes can drop sessionStorage; localStorage survives the auth popup handoff.
  if (isTeamsContext()) {
    localStorage.setItem(SSO_SESSION_INTENT_KEY, '1');
  }
}

function readSsoSessionIntent(): boolean {
  if (sessionStorage.getItem(SSO_SESSION_INTENT_KEY) === '1') {
    return true;
  }

  // localStorage fallback is Teams-only; avoid affecting normal browser login.
  if (
    isTeamsContext() &&
    localStorage.getItem(SSO_SESSION_INTENT_KEY) === '1'
  ) {
    return true;
  }

  return false;
}

function clearSsoSessionIntent(): void {
  sessionStorage.removeItem(SSO_SESSION_INTENT_KEY);
  localStorage.removeItem(SSO_SESSION_INTENT_KEY);
}

export function markSsoSessionIntent(): void {
  persistSsoSessionIntent();
}

export function hasSsoSessionIntent(): boolean {
  return readSsoSessionIntent();
}

export function consumeSsoSessionIntent(): void {
  clearSsoSessionIntent();
}

export function markTeamsSsoInProgress(): void {
  localStorage.setItem(TEAMS_SSO_IN_PROGRESS_KEY, String(Date.now()));
}

export function clearTeamsSsoInProgress(): void {
  localStorage.removeItem(TEAMS_SSO_IN_PROGRESS_KEY);
}

export function markTeamsSsoHandoff(): void {
  if (!isTeamsContext()) {
    return;
  }

  sessionStorage.setItem(TEAMS_SSO_HANDOFF_KEY, '1');
}

export function clearTeamsSsoHandoff(): void {
  sessionStorage.removeItem(TEAMS_SSO_HANDOFF_KEY);
}

export function isTeamsSsoHandoff(): boolean {
  if (!isTeamsContext()) {
    return false;
  }

  return sessionStorage.getItem(TEAMS_SSO_HANDOFF_KEY) === '1';
}

export function markTeamsSsoReloading(): void {
  sessionStorage.setItem(TEAMS_SSO_RELOADING_KEY, '1');
}

export function clearTeamsSsoReloading(): void {
  sessionStorage.removeItem(TEAMS_SSO_RELOADING_KEY);
}

export function isTeamsSsoReloading(): boolean {
  if (!isTeamsContext()) {
    return false;
  }

  return sessionStorage.getItem(TEAMS_SSO_RELOADING_KEY) === '1';
}

export function isTeamsSsoInProgress(): boolean {
  if (!isTeamsContext()) {
    return false;
  }

  const raw = localStorage.getItem(TEAMS_SSO_IN_PROGRESS_KEY);
  if (!raw) return false;

  const startedAt = Number(raw);
  if (Number.isNaN(startedAt)) {
    localStorage.removeItem(TEAMS_SSO_IN_PROGRESS_KEY);
    return false;
  }

  if (Date.now() - startedAt > TEAMS_SSO_IN_PROGRESS_TTL_MS) {
    localStorage.removeItem(TEAMS_SSO_IN_PROGRESS_KEY);
    return false;
  }

  return true;
}

/** Auth0 login / Teams SSO surfaces — leftover sessions may be established here. */
export function isLoginRoute(pathname: string): boolean {
  return (
    pathname === '/login' ||
    pathname === '/soulmap-login' ||
    pathname.startsWith('/soulmap-login/')
  );
}

export function isActivateRoute(pathname: string): boolean {
  return pathname === '/activate' || pathname.startsWith('/activate/');
}

export function isPasswordResetRoute(pathname: string): boolean {
  return (
    pathname.startsWith('/forgot-password') ||
    pathname.includes('/change-password-success')
  );
}

/**
 * Token and password-setup links. These are unauthenticated journeys and must
 * not adopt a leftover Auth0 cookie or bounce the user to /login.
 */
export function isPublicAuthFlowRoute(pathname: string): boolean {
  return isActivateRoute(pathname) || isPasswordResetRoute(pathname);
}

export function shouldAutoEstablishSsoSession(options: {
  pathname: string;
  isAuth0RedirectCallback: boolean;
  isTeamsAuthPopupCallback: boolean;
}): boolean {
  if (options.isTeamsAuthPopupCallback) return true;
  if (options.isAuth0RedirectCallback) return true;
  if (isTeamsSsoInProgress()) return true;
  if (isTeamsSsoHandoff()) return true;
  if (isTeamsSsoReloading()) return true;
  // Reset links never consume an Auth0 cookie.
  if (isPasswordResetRoute(options.pathname)) return false;
  // Login and SSO activate only after this tab started sign-in.
  if (isLoginRoute(options.pathname) || isActivateRoute(options.pathname)) {
    return hasSsoSessionIntent();
  }
  return true;
}

const ACTIVATION_SSO_RETURN_PATH_KEY = 'lift-activation-sso-return-path';
const ACTIVATION_SSO_ERROR_KEY = 'lift-activation-sso-error';

/**
 * Remembers which invitation started SSO so a failed sign-in returns there
 * (with "Try again" / "Set a password instead") rather than to /login. Auth0
 * errors land on the origin without appState, so the path has to live here.
 */
export function markActivationSsoReturnPath(path: string): void {
  try {
    sessionStorage.setItem(ACTIVATION_SSO_RETURN_PATH_KEY, path);
  } catch {
    // Without storage a failed sign-in falls back to /login.
  }
}

/** Safe during render: StrictMode renders twice, so reading must not clear. */
export function readActivationSsoReturnPath(): string | null {
  try {
    const path = sessionStorage.getItem(ACTIVATION_SSO_RETURN_PATH_KEY);
    return path && isActivateRoute(path.split('?')[0]) ? path : null;
  } catch {
    return null;
  }
}

export function consumeActivationSsoReturnPath(): string | null {
  const path = readActivationSsoReturnPath();
  try {
    sessionStorage.removeItem(ACTIVATION_SSO_RETURN_PATH_KEY);
  } catch {
    // Nothing to clear.
  }
  return path;
}

/** Kept out of the URL: backend messages can include the user's email. */
export function markActivationSsoError(message: string): void {
  try {
    sessionStorage.setItem(ACTIVATION_SSO_ERROR_KEY, message);
  } catch {
    // The activation page still shows its generic retry message.
  }
}

export function readActivationSsoError(): string | null {
  try {
    return sessionStorage.getItem(ACTIVATION_SSO_ERROR_KEY);
  } catch {
    return null;
  }
}

export function clearActivationSsoError(): void {
  try {
    sessionStorage.removeItem(ACTIVATION_SSO_ERROR_KEY);
  } catch {
    // Nothing to clear.
  }
}
